/**
 * Plain-language help for every Connections row that is NOT a vault API key:
 * subscriptions signed in on your computer, desktop tools through the paired
 * Local Node, YouTube analytics, storefront payment links, source accounts and
 * planned media vendors. API-key rows use `providerSetupGuides.ts` instead.
 *
 * Text only, keyed by the `connectionsCatalog.ts` row id. Each guide answers
 * "what is this, do I need it, where do I go". Status always comes from the
 * server's readiness projection, never from here, so nothing below claims a
 * row is connected or available. Statements follow `connections/readiness.py`
 * (ROADMAP) and the row's `how` text. Links are the vendors' own sites,
 * checked 2026-10-08.
 */

import type { GuideLink } from './providerSetupGuides';

export interface ConnectionSetupGuide {
  id: string;
  what: string;
  needIt: string;
  whereToGo: string;
  links: GuideLink[];
}

export const CONNECTION_GUIDE_TEXT = {
  toggle: 'What is this and how do I connect it?',
  what: 'What is this?',
  needIt: 'Do I need it?',
  whereToGo: 'Where do I go?',
  links: 'Official links',
} as const;

const PAIR =
  'Pair your computer first: Settings → Local Node → "Pair a Local Node", then keep the Local Node running.';
const PLANNED_NEED = 'Not yet. This is not available in Auteur Cloud yet; the status above says what is planned. You can skip it.';
const PLANNED_WHERE = 'Nothing to do in Auteur for now. When it arrives, this row will change and say how to connect.';

function subscription(id: string, what: string, links: GuideLink[]): ConnectionSetupGuide {
  return {
    id,
    what,
    needIt:
      'Only if you already pay for this subscription and want Auteur to use it for writing or build work on your own seat. ' +
      'It is optional; API keys work without it.',
    whereToGo:
      `${PAIR} Then open Settings → Subscriptions and follow the sign-in there. You sign in on your own computer; Auteur never sees the login.`,
    links,
  };
}

function bridge(id: string, what: string, links: GuideLink[], extra = '', browser = false): ConnectionSetupGuide {
  return {
    id,
    what,
    needIt:
      `Only if you use this tool. It runs on your own computer, not in Auteur Cloud.${extra ? ` ${extra}` : ''} ` +
      'The status above says what works today; pairing your computer now gets you ready.',
    whereToGo: browser
      ? `Sign in on the official site in your own browser on your computer. ${PAIR}`
      : `Install it on your computer from the official site. ${PAIR}`,
    links,
  };
}

function planned(id: string, what: string, links: GuideLink[]): ConnectionSetupGuide {
  return { id, what, needIt: PLANNED_NEED, whereToGo: PLANNED_WHERE, links };
}

const GUIDES: ConnectionSetupGuide[] = [
  // Subscriptions & CLIs
  subscription('claude', 'Your Claude subscription, used through Claude Code on your own computer.', [
    { label: 'Claude Code documentation', href: 'https://code.claude.com/docs/en/overview' },
  ]),
  subscription('chatgpt_codex', 'Your ChatGPT subscription, used through Codex on your own computer.', [
    { label: 'Codex documentation', href: 'https://developers.openai.com/codex' },
  ]),
  subscription('antigravity_gemini', 'Your Gemini access through Google Antigravity on your own computer.', [
    { label: 'Google Antigravity', href: 'https://antigravity.google/' },
  ]),

  // Desktop tools through the paired computer
  bridge('comfyui', 'Open image, video and audio models running on your own graphics card through ComfyUI.', [
    { label: 'ComfyUI', href: 'https://www.comfy.org/' },
  ]),
  bridge('ace_studio', 'ACE Studio 2 for singing and scoring, reached through its own MCP on your computer.', [
    { label: 'ACE Studio', href: 'https://acestudio.ai/' },
  ], 'ACE must be open with MCP turned on in its settings.'),
  bridge('suno', 'Suno for music, as a guided hand-off in your own browser (Suno has no API).', [
    { label: 'Suno', href: 'https://suno.com/' },
  ], '', true),
  bridge('skool', 'Posting to your Skool community from your own logged-in browser (Skool has no API).', [
    { label: 'Skool', href: 'https://www.skool.com/' },
  ], 'Posts stay drafts until you approve them.', true),
  {
    id: 'socials',
    what: "Managed posting to your own LinkedIn, X, Facebook, Instagram and YouTube accounts, through Auteur's posting service.",
    needIt:
      'Yes, if you want Auteur to publish for you. LinkedIn (personal profiles) and X work now. Facebook and Instagram ' +
      'need tester access while Meta reviews our app: ask support to add you, accept the tester invitation in Facebook, ' +
      'and note that posts stay hidden from the public until the review passes. YouTube needs you added as a test user ' +
      'and uploads stay private until Google verification completes. TikTok, Threads and Pinterest are not available yet. ' +
      'A post publishes only after you approve it in your workspace.',
    whereToGo:
      'Here: open this row, press Connect for a platform, sign in on its own page in the new tab, then press ' +
      "\"I've finished connecting\". Then, in your workspace, Social → Post now or Schedule an approved package.",
    links: [{ label: 'Postiz', href: 'https://postiz.com/' }],
  },
  bridge('resolve', 'Sending timelines and markers from Audiovisual Canvas to DaVinci Resolve on your computer.', [
    { label: 'DaVinci Resolve', href: 'https://www.blackmagicdesign.com/products/davinciresolve' },
  ], 'Resolve must be open.'),

  // Publishing
  {
    id: 'youtube',
    what:
      'Read-only YouTube channel analytics through a Google grant. Uploading is a separate capability with its own status.',
    needIt: 'Only if you want your channel\'s analytics in Auteur. Nothing is ever posted without your approval.',
    whereToGo:
      'Open your workspace\'s Social tab to connect, check or revoke the read-only grant (if the status says analytics is not configured on this deployment, it cannot be connected yet). You can also remove access any time from your Google account\'s third-party access page.',
    links: [
      { label: 'YouTube Studio', href: 'https://studio.youtube.com/' },
      { label: 'Google account: third-party access', href: 'https://myaccount.google.com/permissions' },
    ],
  },

  // Commerce
  {
    id: 'stripe_payment_links',
    what: 'A Stripe Payment Link: a checkout page on Stripe that you create, used for offers and buy buttons in your storefront.',
    needIt: 'Only if you sell something from a storefront you build in Launch. It is not how you pay for Auteur (that is Billing).',
    whereToGo:
      'Create a Payment Link in your Stripe Dashboard and copy its link, then open your workspace → Launch → Commerce & Forms and paste it there. Auteur checks the link; your customers pay on Stripe and Auteur never sees card details.',
    links: [
      { label: 'Stripe Payment Links guide', href: 'https://docs.stripe.com/payment-links' },
      { label: 'Stripe Dashboard: Payment Links', href: 'https://dashboard.stripe.com/payment-links' },
    ],
  },
  {
    id: 'paypal_payment_links',
    what: 'A PayPal payment link you create in PayPal, used for offers and buy buttons in your storefront.',
    needIt: 'Only if you sell something from a storefront you build in Launch. It is not how you pay for Auteur (that is Billing).',
    whereToGo:
      'Create a payment link in your PayPal business account and copy it, then open your workspace → Launch → Commerce & Forms and paste it there. Auteur checks the link; your customers pay on PayPal.',
    links: [
      { label: 'PayPal payment links', href: 'https://www.paypal.com/us/business/accept-payments/payment-links' },
    ],
  },

  // Sources (planned account links)
  planned('google_drive', 'Importing documents and files from Google Drive and Docs into Intake.', [{ label: 'Google Drive', href: 'https://drive.google.com/' }]),
  planned('dropbox', 'Importing files from Dropbox into Intake.', [{ label: 'Dropbox', href: 'https://www.dropbox.com/' }]),
  planned('onedrive', 'Importing documents and files from OneDrive into Intake.', [{ label: 'OneDrive', href: 'https://onedrive.live.com/' }]),
  planned('notion', 'Importing pages and notes from Notion into Intake.', [{ label: 'Notion', href: 'https://www.notion.so/' }]),
  planned('github', 'Importing repositories and docs from GitHub into Intake.', [{ label: 'GitHub', href: 'https://github.com/' }]),

  // Image & video vendors on their official routes (planned)
  planned('kling', 'Kling video and image models in Visual Canvas, with your Kling API key or your own Kling account.', [{ label: 'Kling AI', href: 'https://kling.ai/' }]),
  planned('higgsfield', 'Higgsfield video, image, relight and upscale in Visual Canvas.', [{ label: 'Higgsfield', href: 'https://higgsfield.ai/' }]),
  planned('runway', 'Runway models in Visual Canvas, with your Runway API key or your own Runway account.', [{ label: 'Runway', href: 'https://runwayml.com/' }]),
  planned('openart', 'Your own OpenArt account in Visual Canvas, through OpenArt\'s official CLI.', [{ label: 'OpenArt', href: 'https://openart.ai/' }]),
  planned('google_flow', 'Google Veo and Nano Banana in Visual Canvas, through a Gemini API key (Flow itself has no customer API).', [{ label: 'Google Flow', href: 'https://labs.google/flow' }]),
  planned('ltx_studio', 'LTX video models in Visual Canvas, with your LTX API key.', [{ label: 'LTX Studio', href: 'https://ltx.studio/' }]),
  planned('magnific', 'Magnific image upscaling in Enhance, with your Magnific API key.', [{ label: 'Magnific', href: 'https://magnific.ai/' }]),
  planned('topaz', 'Topaz Labs image and video upscaling in Enhance, with your Topaz API key.', [{ label: 'Topaz Labs', href: 'https://www.topazlabs.com/' }]),
  {
    id: 'midjourney',
    what: 'Midjourney stills, style and character references.',
    needIt: 'It cannot be connected: Midjourney has no public API and is not offered in hosted workspaces.',
    whereToGo: 'Nothing to do in Auteur.',
    links: [{ label: 'Midjourney', href: 'https://www.midjourney.com/' }],
  },
];

export const CONNECTION_SETUP_GUIDES: Readonly<Record<string, ConnectionSetupGuide>> = Object.freeze(
  Object.fromEntries(GUIDES.map((guide) => [guide.id, guide])),
);

/** The guide for a Connections row id, or null. */
export function connectionGuideFor(id: string | null | undefined): ConnectionSetupGuide | null {
  if (!id) return null;
  return Object.prototype.hasOwnProperty.call(CONNECTION_SETUP_GUIDES, id) ? CONNECTION_SETUP_GUIDES[id] : null;
}
